/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import React, { useState } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { Search } from 'lucide-react';

import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

import { Input } from '../../ux-common/src/components/ui/input';
import { DetailsPageSpinner } from '../spinner/details-page-spinner';
import { Command, CommandList } from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ScrollArea, ScrollBar } from '../ui/scroll-area';

export interface RadioOption {
  label: string;
  value: string;
}

interface EnhancedRadioSelectI {
  options: RadioOption[];
  onUpdate: (selected: RadioOption | null) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  modalPopover?: boolean;
  children: React.ReactNode;
  loading: boolean;
  finalValue: RadioOption | null;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  alignOffset?: number;
}

const EnhancedRadioSelect = ({
  options,
  onUpdate,
  isOpen,
  setIsOpen,
  modalPopover = false,
  children,
  loading,
  finalValue,
  side = 'left',
  align = 'start',
  sideOffset = 0,
  alignOffset = 0,
}: EnhancedRadioSelectI) => {
  const { messages } = useLanguage();
  const langData = messages?.PromptFilters;

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOption, setSelectedOption] = useState<RadioOption | null>(
    finalValue
  );

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleApply = () => {
    onUpdate(selectedOption);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setSelectedOption(finalValue);
    setIsOpen(false);
  };

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          setSelectedOption(finalValue);
        }
      }}
      modal={modalPopover}
    >
      <PopoverTrigger asChild>
        {children && <div>{children}</div>}
      </PopoverTrigger>

      <PopoverContent
        className="max-w-[300px] p-0 text-[#323130]"
        align={align}
        side={side}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        onEscapeKeyDown={() => setIsOpen(false)}
      >
        <Command className="rounded-none">
          {/* Search Input */}
          <div className="border-b border-[#C1C9CF] bg-white">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 transform text-form-input-disabled text-gray-400 opacity-50" />
              <Input
                type="text"
                placeholder="Search by name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-8 rounded-none border-none bg-white pl-10 text-[13px] text-black focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </div>
          </div>

          {/* Radio Options */}
          <CommandList className="bg-white">
            {loading ? (
              <div className="m-3 flex justify-center">
                <DetailsPageSpinner />
              </div>
            ) : options.length === 0 ? (
              <div className="m-3 text-center text-black">
                <p>{messages?.General?.no_data_found}</p>
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="m-3 text-center text-black">
                <p>{langData?.multi_select.no_results_found}</p>
              </div>
            ) : (
              <RadioGroup
                value={selectedOption?.value || ''}
                onValueChange={(value) => {
                  const found = options.find((opt) => opt.value === value);
                  setSelectedOption(found || null);
                }}
              >
                <ScrollArea className="h-40 max-w-[300px]">
                  {filteredOptions.map((option) => (
                    <div
                      key={option.value}
                      className="flex h-8 cursor-pointer items-center space-x-2 px-2 text-[#323130] hover:bg-[#F3F2F1]"
                      onClick={() => setSelectedOption(option)}
                    >
                      <RadioGroupItem
                        value={option.value}
                        id={option.value}
                        className={`${
                          selectedOption?.value === option.value
                            ? 'border-[#34BCAF] bg-[#34BCAF]'
                            : 'border-[#C1C9CF]'
                        }`}
                      />
                      <Label
                        htmlFor={option.value}
                        className="block flex-1 cursor-pointer overflow-hidden truncate text-ellipsis whitespace-nowrap font-normal text-[#323130]"
                      >
                        {option.label}
                      </Label>
                    </div>
                  ))}
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>
              </RadioGroup>
            )}
          </CommandList>
        </Command>

        {/* Action Buttons */}
        <div className="flex h-9 items-center gap-2.5 border-t border-[#C1C9CF] bg-white px-2 py-[6px]">
          <button
            onClick={handleApply}
            disabled={!selectedOption}
            className="h-6 rounded-sm px-2 text-[13px] font-medium text-[#0A2D3A] transition-colors hover:bg-[#F3F2F1] disabled:text-form-input-disabled"
          >
            {langData?.multi_select.apply}
          </button>
          <div className="h-[16px] w-px bg-[#C1C9CF]" />
          <button
            onClick={handleCancel}
            className="h-6 rounded-sm px-2 text-[13px] font-medium text-[#0A2D3A] transition-colors hover:bg-[#F3F2F1]"
          >
            {messages?.General.cancel}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default EnhancedRadioSelect;
